import { memo, useEffect, useMemo, useRef, useState } from 'react'

import { Tokens } from '@/assets/tokens'

import { Alert, Api, Confirmation } from '@/modules'

import { Button, Divider, Flex, Switch } from '@/components'
import { IconTooltip } from '@/components/core'
import { ColumnType, ContentFilter, Table } from '@/components/derived'
import { Info } from '@icon-park/react'

import { useRouting } from '@/hooks'
import { getFromToElements } from '@/utils/pagination'
import { SpokespersonResponse, ToggleSpokespersonRequest } from '@shared/models'
import { useCompanyContext } from '../CompanyDetails.context'

interface TableContent {
  id: string
  name: string
  brandID?: string
  scoringBrands: string
  cenaValue: 'SIM' | 'NÃO'
}

export const SpokespersonsTable = memo(Component)

function Component() {
  const { currentPage, changeQueryParam } = useRouting()
  const { isLoadingSpokespeople, isLoadingAllBrands, spokespeople, allBrands, reloadSpokesPeople } = useCompanyContext()
  const [onlyActive, setOnlyActive] = useState(true)
  const [nameFilter, setNameFilter] = useState('')
  const prevOnlyActiveRef = useRef(onlyActive)
  const isAdjustingPageRef = useRef(false)

  const { sendAlert } = Alert.Hook()
  const { open: openConfirmation } = Confirmation.Hook({
    text: 'Tem certeza que deseja desativar este porta-voz?',
    confirmText: 'Sim, desativar',
  })

  const { run: toggleSpokesperson } = Api.Command<void>(Api.Methods.PATCH, 'spokespersons/:id/toggle')

  const brandNamesById = useMemo(() => {
    return new Map(allBrands.map((brand) => [brand.id, brand.name]))
  }, [allBrands])

  useEffect(() => {
    const onlyActiveChanged = prevOnlyActiveRef.current !== onlyActive
    prevOnlyActiveRef.current = onlyActive

    if (onlyActiveChanged && currentPage !== '1') {
      isAdjustingPageRef.current = true
      changeQueryParam({
        param: 'pagina',
        value: '1',
      })
      return
    }

    if (isAdjustingPageRef.current) {
      isAdjustingPageRef.current = false
    }

    reloadSpokesPeople(currentPage, onlyActive, nameFilter || undefined)
  }, [currentPage, onlyActive, nameFilter])

  function handleEditSpokesperson(spokespersonId: string, brandID?: string) {
    changeQueryParam({
      param: 'editSpokespersonId',
      value: spokespersonId,
    })

    changeQueryParam({
      param: 'editSpokespersonBrandId',
      value: brandID,
    })
  }

  function adjustPageIfNeeded(totalAfterAction: number, pageSize: number = 10): boolean {
    const currentPageNum = Number(currentPage)
    const totalPages = Math.ceil(totalAfterAction / pageSize)

    if (currentPageNum > totalPages && totalPages > 0) {
      isAdjustingPageRef.current = true
      changeQueryParam({
        param: 'pagina',
        value: String(totalPages),
      })
      return true
    }
    return false
  }

  function handleToggle(spokesperson: SpokespersonResponse, newState: boolean): void {
    const executeToggle = () => {
      toggleSpokesperson<ToggleSpokespersonRequest>({
        binds: [['id', spokesperson.id]],
        input: { isActive: newState },
        onSuccess: () => {
          sendAlert({
            type: 'success',
            title: newState ? 'Porta-voz ativado' : 'Porta-voz desativado',
            message: `Porta-voz ${newState ? 'ativado' : 'desativado'} com sucesso`,
          })

          const willRemoveFromList = (onlyActive && !newState) || (!onlyActive && newState)
          let pageWasAdjusted = false

          if (willRemoveFromList && spokespeople?.total) {
            pageWasAdjusted = adjustPageIfNeeded(spokespeople.total - 1)
          }

          if (!pageWasAdjusted) {
            reloadSpokesPeople(currentPage, onlyActive, nameFilter || undefined)
          }
        },
      })
    }

    if (!newState) {
      openConfirmation(executeToggle)
    } else {
      executeToggle()
    }
  }

  const columns: ColumnType<TableContent>[] = [
    {
      key: 'name',
      title: 'Nome',
    },
    {
      key: 'scoringBrands',
      title: 'Marcas',
      render: (_, item) => item.scoringBrands,
    },
    {
      key: 'cenaValue',
      title: (
        <Flex $type="row" $align="center" $gap={4} $justify="center">
          CENA
          <IconTooltip
            icon={<Info theme="outline" size={12} />}
            tooltipText="Indica se o porta-voz possui marca(s) selecionada(s) no CENA"
          />
        </Flex>
      ),
      render: (_, item) => item.cenaValue,
    },
    {
      key: 'options',
      title: 'Opções',
      width: 50,
      align: 'center',
      render: (_, item) => {
        const spokesperson = spokespeople?.elements.find((s) => s.id === item.id)
        return (
          <Flex $type="row" $justify="flex-start" $align="stretch">
            <Button.Root
              $variant="link"
              style={{ color: Tokens.ColorUIPrimary }}
              onClick={() => handleEditSpokesperson(item.id, item.brandID)}
              disabled={!spokesperson?.isActive}>
              Editar
            </Button.Root>
          </Flex>
        )
      },
    },
    {
      key: 'active',
      title: 'Ativo',
      align: 'center',
      width: 50,
      render: (_, item) => {
        const spokesperson = spokespeople?.elements.find((s) => s.id === item.id)
        const handleSwitchToggle = () => {
          if (spokesperson) {
            const newState = !spokesperson.isActive
            handleToggle(spokesperson, newState)
          }
        }
        return <Switch.Root isChecked={spokesperson?.isActive ?? true} toggle={handleSwitchToggle} />
      },
    },
  ]

  const sanitizedSpokespersons = useMemo<TableContent[]>(() => {
    if (!spokespeople?.elements?.length) return []

    return spokespeople.elements.flatMap((item): TableContent[] => {
      if (!item.scoringBrandIDs?.length) {
        return [
          {
            id: item.id,
            name: item.name,
            brandID: undefined,
            scoringBrands: 'Nenhuma marca',
            cenaValue: 'NÃO',
          },
        ]
      }

      return item.scoringBrandIDs.map((brandID) => {
        const brandName =
          allBrands.length ? (brandNamesById.get(brandID) ?? 'Marca não encontrada') : 'Carregando marcas...'
        return {
          id: item.id,
          name: item.name,
          brandID,
          scoringBrands: brandName,
          cenaValue: item.cenaBrandIDs?.includes(brandID) ? 'SIM' : 'NÃO',
        }
      })
    })
  }, [spokespeople?.elements, allBrands.length, brandNamesById])

  return (
    <>
      <Divider.Root>
        <Divider.Title value="Porta-vozes registrados" />
        <Divider.Spacer />
        <Flex $type="row" $align="center" $gap={12}>
          <Divider.Subtitle
            value={`${getFromToElements(currentPage, sanitizedSpokespersons.length)}/${sanitizedSpokespersons.length} Entrada(s)`}
          />
        </Flex>
      </Divider.Root>
      <Divider.Root>
        <ContentFilter
          isLoading={isLoadingSpokespeople}
          onApply={(content) => {
            setNameFilter(content?.trim() || '')
            if (currentPage !== '1') {
              isAdjustingPageRef.current = true
              changeQueryParam({
                param: 'pagina',
                value: '1',
              })
            }
          }}
          onReset={() => {
            setNameFilter('')
            if (currentPage !== '1') {
              isAdjustingPageRef.current = true
              changeQueryParam({
                param: 'pagina',
                value: '1',
              })
            }
          }}
          placeholder="Porta-voz">
          <Flex $type="row" $align="center" $gap={8}>
            <Switch.Root
              isChecked={onlyActive}
              toggle={() => setOnlyActive(!onlyActive)}
              label="Apenas ativos"
              direction="row"
            />
          </Flex>
        </ContentFilter>
      </Divider.Root>
      <Table
        isLoading={isLoadingSpokespeople || isLoadingAllBrands}
        columns={columns}
        data={sanitizedSpokespersons}
        totalPages={spokespeople?.pages}
      />
    </>
  )
}
